/**
 * JSON import/export utilities.
 * Handles programme data serialization for file download and upload.
 * @module export/json
 */

import type { Programme } from "@/types";
import { downloadBlob } from "@/utils/dom";
import { migrateProgramme } from "@/utils/migrate-programme";

/**
 * Downloads a programme as a JSON file.
 * Creates a downloadable blob with formatted JSON content.
 */
export function downloadJson(programme: Programme): void {
  const title = programme.title?.trim() || "programme";
  const filename = `${title.replace(/\s+/g, "_")}.json`;
  const blob = new Blob([JSON.stringify(programme, null, 2)], {
    type: "application/json",
  });
  downloadBlob(blob, filename);
}

/**
 * Reads and parses a JSON file.
 * @throws {SyntaxError} If JSON is invalid
 */
export async function importJson(file: File): Promise<object> {
  const text = await file.text();
  return JSON.parse(text);
}

/**
 * Detects a governed JSON export envelope: an object carrying a `programme`
 * payload alongside `metadata`. Such envelopes wrap (rather than mutate) the
 * programme, so importing must unwrap to the inner `programme` value.
 */
function isGovernedJsonEnvelope(value: object): value is { metadata: unknown; programme: object } {
  return (
    "metadata" in value &&
    "programme" in value &&
    typeof (value as { programme: unknown }).programme === "object" &&
    (value as { programme: unknown }).programme !== null
  );
}

/**
 * Imports and validates a programme from a JSON file.
 * Applies schema migrations to bring legacy data up to current version.
 */
export async function importProgrammeFromJson(
  file: File,
): Promise<{ success: boolean; programme?: object; error?: string }> {
  try {
    const text = await file.text();
    const parsed = JSON.parse(text);

    // Basic validation
    if (!parsed || typeof parsed !== "object") {
      return { success: false, error: "Invalid JSON structure" };
    }

    // Unwrap a governed JSON export envelope ({ metadata, programme }) to its
    // programme payload; a bare programme (e.g. samples/*.json) is used as-is.
    const programme = isGovernedJsonEnvelope(parsed) ? parsed.programme : parsed;

    // Apply migrations to bring data to current schema version
    const migrated = migrateProgramme(programme);

    return { success: true, programme: migrated };
  } catch (e) {
    return { success: false, error: (e as Error).message };
  }
}
